/**
 * SoleneLife Cart Drawer — Behavior Module
 * Handles: cart rendering, quantity changes, remove with undo,
 * free shipping progress bar, context-aware upsell, promo codes,
 * auto-open on add-to-cart, mobile drag-to-close.
 *
 * Listens for Shopify Standard Events (shopify:cart:lines-update) for auto-open.
 * Uses Cart AJAX API (/cart.js, /cart/change.js, /cart/update.js, /cart/add.js).
 */

(function () {
  'use strict';

  // ─── Config ────────────────────────────────────────────────────────
  const container = document.querySelector('[data-solene-cart-drawer]');
  if (!container) return;

  const config = {
    moneyFormat: container.dataset.moneyFormat || '${{amount}}',
    freeShippingThreshold: parseInt(container.dataset.freeShippingThreshold, 10) || 5000,
    drawerContext: container.dataset.drawerContext || 'solene',
    productHandle: container.dataset.productHandle,
    productUrl: container.dataset.productUrl,
    variantId: container.dataset.variantId,
    productTitle: container.dataset.productTitle,
    productImage: container.dataset.productImage,
    productPrice: parseInt(container.dataset.productPrice, 10) || 3995,
    productComparePrice: parseInt(container.dataset.productComparePrice, 10) || 7990,
    bogoOfferToken: container.dataset.bogoOfferToken || 'subscribe-bogo',
    bogoPrice: parseInt(container.dataset.bogoPrice, 10) || 3999,
    rating: parseFloat(container.dataset.rating) || 4.8,
    reviewCount: container.dataset.reviewCount || '3,847',
  };

  // ─── DOM refs ──────────────────────────────────────────────────────
  const refs = {
    items: container.querySelector('[data-solene-cd-items]'),
    body: container.querySelector('[data-solene-cd-body]'),
    count: container.querySelector('[data-solene-cd-count]'),
    subtotal: container.querySelector('[data-solene-cd-subtotal]'),
    savings: container.querySelector('[data-solene-cd-savings]'),
    savingsAmount: container.querySelector('[data-solene-cd-savings-amount]'),
    shipping: container.querySelector('[data-solene-cd-shipping]'),
    checkout: container.querySelector('[data-solene-cd-checkout]'),
    footer: container.querySelector('[data-solene-cd-footer]'),
    empty: container.querySelector('[data-solene-cd-empty]'),
    progress: container.querySelector('[data-solene-cd-progress]'),
    progressMsg: container.querySelector('[data-solene-cd-progress-msg]'),
    progressFill: container.querySelector('[data-solene-cd-progress-fill]'),
    upsellWrap: container.querySelector('[data-solene-cd-upsell-wrap]'),
    upsellCard: container.querySelector('[data-solene-cd-upsell-card]'),
    close: container.querySelector('[data-solene-cd-close]'),
    handle: container.querySelector('[data-solene-cd-handle]'),
  };

  let lastRemovedItem = null;
  let undoTimer = null;
  let isFirstRender = true;

  // ─── Money formatting ──────────────────────────────────────────────
  function formatMoney(cents, format) {
    if (typeof cents === 'string') cents = cents.replace('.', '');
    cents = parseInt(cents, 10) || 0;
    const value = (cents / 100).toFixed(2);
    const valueNoDecimals = String(Math.round(cents / 100));
    const fmt = format || config.moneyFormat;
    return fmt
      .replace('{{amount_with_comma_separator}}', value.replace('.', ','))
      .replace('{{amount_no_decimals_with_comma_separator}}', valueNoDecimals.replace(/\B(?=(\d{3})+(?!\d))/g, '.'))
      .replace('{{amount_no_decimals}}', valueNoDecimals)
      .replace('{{amount}}', value);
  }

  // ─── Cart API ──────────────────────────────────────────────────────
  // Use Shopify.routes.root for locale-aware URLs. Use /cart.json for GET
  // to avoid a 301 redirect to the canonical domain that causes CORS errors
  // on preview/staging domains.
  const cartRoot = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

  async function fetchCart() {
    const res = await fetch(cartRoot + 'cart.json', { headers: { 'Accept': 'application/json' } });
    if (!res.ok) throw new Error('Failed to fetch cart');
    return res.json();
  }

  async function changeCart(key, quantity) {
    const res = await fetch(cartRoot + 'cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ id: key, quantity: quantity }),
    });
    if (!res.ok) throw new Error('Failed to update cart');
    return res.json();
  }

  async function addToCart(variantId, quantity, properties) {
    const body = { id: variantId, quantity: quantity };
    if (properties) body.properties = properties;
    const res = await fetch(cartRoot + 'cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify(body),
    });
    if (!res.ok) throw new Error('Failed to add to cart');
    return res.json();
  }

  async function applyDiscountCode(code) {
    const res = await fetch(cartRoot + 'cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ discounts: [{ code: code }] }),
    });
    if (!res.ok) throw new Error('Failed to apply discount');
    return res.json();
  }

  // ─── BOGO detection ────────────────────────────────────────────────
  function isBogoItem(item) {
    if (!item.properties) return false;
    const fudge = item.properties._fudge;
    if (!fudge) return false;
    try {
      const parsed = JSON.parse(fudge);
      return parsed.offer === config.bogoOfferToken;
    } catch (e) {
      return String(fudge).includes(config.bogoOfferToken);
    }
  }

  function isOneTimeItem(item) {
    return !isBogoItem(item) && !item.selling_plan_allocation;
  }

  function isFreeBogoItem(item) {
    return isBogoItem(item) && (item.final_price === 0 || item.final_line_price === 0);
  }

  // ─── Escape HTML ───────────────────────────────────────────────────
  function esc(str) {
    const div = document.createElement('div');
    div.textContent = String(str || '');
    return div.innerHTML;
  }

  // ─── Render: Items ─────────────────────────────────────────────────
  function renderItems(cart) {
    if (!cart.items || cart.items.length === 0) {
      refs.items.innerHTML = '';
      return;
    }

    let html = '';
    for (const item of cart.items) {
      const bogo = isBogoItem(item);
      const freeBogo = isFreeBogoItem(item);
      const img = item.image
        ? `<img src="${esc(item.image)}&width=192" alt="${esc(item.product_title)}" width="96" height="96" loading="lazy" style="width:100%;height:100%;object-fit:cover;display:block;">`
        : '';
      const itemUrl = esc(item.url || '');

      // Plan label
      let planLabel = 'One-time purchase';
      if (bogo) {
        planLabel = 'Subscribe &amp; Save &#8212; Buy 1 Get 1 FREE';
      } else if (item.selling_plan_allocation) {
        planLabel = esc(item.selling_plan_allocation.selling_plan.name);
      }

      // Price display
      let priceHtml = '';
      if (item.original_price !== item.final_price) {
        priceHtml = `<s>${formatMoney(item.original_price)}</s>${formatMoney(item.final_price)}`;
      } else {
        priceHtml = formatMoney(item.original_price);
      }

      // Free BOGO item: render as locked FREE line
      if (freeBogo) {
        html += `
          <div class="solene-cd-item solene-cd-item--free" data-key="${esc(item.key)}" data-quantity="${item.quantity}" data-variant-id="${item.variant_id}" data-is-bogo="true" data-is-free="true">
            <div class="solene-cd-item-img">${img}</div>
            <div class="solene-cd-item-info">
              <span class="solene-cd-item-name">${esc(item.product_title)}</span>
              <div class="solene-cd-item-plan" style="color: var(--sl-sage); font-weight: 600;">FREE GIFT &#8212; Buy 1 Get 1</div>
              <div class="solene-cd-item-bottom">
                <div class="solene-cd-item-price solene-cd-item-price--free">FREE</div>
                <div class="solene-cd-item-controls">
                  <div class="solene-cd-qty" style="opacity: 0.5; pointer-events: none;">
                    <span class="solene-cd-qty-count">${item.quantity}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>`;
        continue;
      }

      // Standard or paid BOGO item
      html += `
        <div class="solene-cd-item" data-key="${esc(item.key)}" data-quantity="${item.quantity}" data-variant-id="${item.variant_id}" data-is-bogo="${bogo}">
          <div class="solene-cd-item-img">${img}</div>
          <div class="solene-cd-item-info">
            <a href="${itemUrl}" class="solene-cd-item-name">${esc(item.product_title)}</a>
            <div class="solene-cd-item-plan">${planLabel}</div>
            <div class="solene-cd-item-rating">
              <span class="solene-cd-stars">${starsHtml(config.rating)}</span>
              <span>${config.rating} (${config.reviewCount})</span>
            </div>
            <div class="solene-cd-item-bottom">
              <div class="solene-cd-item-price">${priceHtml}</div>
              <div class="solene-cd-item-controls">
                <div class="solene-cd-qty">
                  <button class="solene-cd-qty-btn" data-solene-cd-qty-minus type="button" aria-label="Decrease quantity">&#8722;</button>
                  <span class="solene-cd-qty-count" data-solene-cd-qty-count>${item.quantity}</span>
                  <button class="solene-cd-qty-btn" data-solene-cd-qty-plus type="button" aria-label="Increase quantity">+</button>
                </div>
                <button class="solene-cd-item-remove" data-solene-cd-remove type="button" aria-label="Remove item">
                  ${trashIconHtml()}
                </button>
              </div>
            </div>
          </div>
        </div>`;
    }

    refs.items.innerHTML = html;
  }

  // ─── Star rating HTML ──────────────────────────────────────────────
  function starsHtml(rating) {
    let html = '';
    for (let i = 1; i <= 5; i++) {
      const filled = rating >= i;
      const half = !filled && rating > i - 1;
      const fill = filled ? 'var(--sl-gold)' : half ? 'var(--sl-gold)' : 'var(--sl-line)';
      html += `<svg width="12" height="12" viewBox="0 0 24 24" style="flex-shrink:0;" aria-hidden="true"><path fill="${fill}" d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/></svg>`;
    }
    return html;
  }

  // ─── Trash icon HTML ───────────────────────────────────────────────
  function trashIconHtml() {
    return `<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><polyline points="3 6 5 6 21 6"></polyline><path d="M19 6l-2 14a2 2 0 0 1-2 2H9a2 2 0 0 1-2-2L5 6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path></svg>`;
  }

  // ─── Update header cart badge ──────────────────────────────────────
  function updateCartBadge(itemCount) {
    const bubbleCount = document.querySelector('[data-testid="cart-bubble"]');
    const bubble = document.querySelector('.cart-bubble');
    const cartIcon = document.querySelector('.sl-site-cart-icon');

    if (bubbleCount) {
      bubbleCount.textContent = itemCount;
      bubbleCount.classList.toggle('hidden', itemCount === 0);
    }
    if (bubble) {
      bubble.classList.toggle('visually-hidden', itemCount === 0);
    }
    if (cartIcon) {
      cartIcon.classList.toggle('header-actions__cart-icon--has-cart', itemCount > 0);
    }
  }

  // ─── Render: Footer (subtotal, savings, shipping) ──────────────────
  function renderFooter(cart) {
    const subtotal = cart.total_price;
    const originalSubtotal = cart.items.reduce((sum, item) => sum + (item.original_line_price || (item.original_price * item.quantity) || 0), 0);
    const savings = originalSubtotal - subtotal;

    // Subtotal
    if (savings > 0) {
      refs.subtotal.innerHTML = `<s>${formatMoney(originalSubtotal)}</s>${formatMoney(subtotal)}`;
      refs.savingsAmount.textContent = formatMoney(savings);
      refs.savings.hidden = false;
    } else {
      refs.subtotal.textContent = formatMoney(subtotal);
      refs.savings.hidden = true;
    }

    // Shipping expectation
    const hasBogo = cart.items.some(isBogoItem);
    const isOverThreshold = hasBogo || subtotal >= config.freeShippingThreshold;
    if (isOverThreshold) {
      refs.shipping.className = 'solene-cd-shipping solene-cd-shipping--free';
      refs.shipping.innerHTML = `${checkIconHtml()} Free shipping &middot; Arrives in 2&#8211;7 business days`;
    } else {
      refs.shipping.className = 'solene-cd-shipping';
      refs.shipping.textContent = 'Shipping calculated at checkout \u00b7 Free over $50';
    }

    // Item count
    refs.count.textContent = cart.item_count;
    updateCartBadge(cart.item_count);

    // Checkout button
    refs.checkout.disabled = cart.item_count === 0;
  }

  // ─── Check icon HTML ───────────────────────────────────────────────
  function checkIconHtml() {
    return `<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round" style="flex-shrink:0;" aria-hidden="true"><polyline points="20 6 9 17 4 12"></polyline></svg>`;
  }

  // ─── Render: Free shipping progress bar ────────────────────────────
  function renderProgressBar(cart) {
    if (!cart.items || cart.items.length === 0) {
      refs.progress.hidden = true;
      return;
    }

    refs.progress.hidden = false;
    const subtotal = cart.total_price;
    const threshold = config.freeShippingThreshold;
    const hasBogo = cart.items.some(isBogoItem);
    const remaining = hasBogo ? 0 : threshold - subtotal;
    const pct = hasBogo ? 100 : Math.min(100, (subtotal / threshold) * 100);

    // Set fill width
    refs.progressFill.style.width = pct + '%';

    // Message
    if (remaining <= 0) {
      // Unlocked
      refs.progressMsg.innerHTML = `<span class="solene-cd-progress-unlocked">${checkIconHtml()} You&#39;ve unlocked free shipping</span>`;
      refs.progressFill.classList.add('solene-cd-progress-fill--pulse');
      setTimeout(() => refs.progressFill.classList.remove('solene-cd-progress-fill--pulse'), 600);
    } else {
      refs.progressFill.classList.remove('solene-cd-progress-fill--pulse');
      const remainingFormatted = formatMoney(remaining);
      if (pct < 60) {
        refs.progressMsg.innerHTML = `Add <span class="solene-cd-progress-amount">${remainingFormatted}</span> more for free shipping`;
      } else {
        refs.progressMsg.innerHTML = `You&#39;re only <span class="solene-cd-progress-amount">${remainingFormatted}</span> away from free shipping!`;
      }
    }
  }

  // ─── Render: Upsell card ───────────────────────────────────────────
  function renderUpsell(cart) {
    if (!cart.items || cart.items.length === 0) {
      refs.upsellWrap.hidden = true;
      return;
    }

    // On the diffuser landing page, don't push the estriol cream upsell
    if (config.drawerContext === 'sldp') {
      refs.upsellWrap.hidden = true;
      return;
    }

    const hasBogo = cart.items.some(isBogoItem);
    const hasOneTime = cart.items.some(isOneTimeItem);
    const subtotal = cart.total_price;
    const overThreshold = subtotal >= config.freeShippingThreshold;

    // Priority a: one-time purchase → subscription upgrade
    if (hasOneTime) {
      const oneTimeItem = cart.items.find(isOneTimeItem);
      const currentPrice = oneTimeItem.final_price || oneTimeItem.price;
      const totalSavings = config.productPrice; // Value of the free second tube

      refs.upsellCard.innerHTML = `
        <div class="solene-cd-upsell-img"><img src="${esc(config.productImage)}&width=128" alt="${esc(config.productTitle)}" style="width:100%;height:100%;object-fit:cover;"></div>
        <div class="solene-cd-upsell-info">
          <div class="solene-cd-upsell-name">Switch to Subscribe &amp; Save</div>
          <div class="solene-cd-upsell-reason">Buy 1 Get 1 FREE. Get a 2nd tube FREE (save ${formatMoney(Math.max(0, totalSavings))}), pause or cancel anytime.</div>
          <div class="solene-cd-upsell-price">${formatMoney(config.bogoPrice)} for 2</div>
        </div>
        <button class="solene-cd-upsell-add" data-solene-cd-upsell-action="switch" data-key="${esc(oneTimeItem.key)}" data-variant-id="${oneTimeItem.variant_id}">Switch &amp; Save</button>`;
      refs.upsellWrap.hidden = false;
      return;
    }

    // Priority b: below threshold → bridge product
    if (hasBogo && !overThreshold) {
      const remaining = config.freeShippingThreshold - subtotal;
      refs.upsellCard.innerHTML = `
        <div class="solene-cd-upsell-img"><img src="${esc(config.productImage)}&width=128" alt="${esc(config.productTitle)}" style="width:100%;height:100%;object-fit:cover;"></div>
        <div class="solene-cd-upsell-info">
          <div class="solene-cd-upsell-name">Add a second tube</div>
          <div class="solene-cd-upsell-reason">Results build over 60 days &#8212; add a second tube to stay on track.</div>
          <div class="solene-cd-upsell-price">${formatMoney(config.bogoPrice)}</div>
        </div>
        <button class="solene-cd-upsell-add" data-solene-cd-upsell-action="bridge" data-variant-id="${config.variantId}">+ Add</button>`;
      refs.upsellWrap.hidden = false;
      return;
    }

    // Priority c: subscribed AND over threshold → no upsell
    refs.upsellWrap.hidden = true;
  }

  // ─── Render: Empty / filled state ──────────────────────────────────
  function renderState(cart) {
    const isEmpty = !cart.items || cart.items.length === 0;
    refs.empty.hidden = !isEmpty;
    refs.footer.hidden = isEmpty;
    refs.progress.hidden = isEmpty;
    refs.body.hidden = isEmpty;
  }

  // ─── Full render ───────────────────────────────────────────────────
  async function fullRender(skipItems) {
    try {
      const cart = await fetchCart();
      if (!skipItems) renderItems(cart);
      renderFooter(cart);
      renderProgressBar(cart);
      renderUpsell(cart);
      renderState(cart);
      return cart;
    } catch (err) {
      console.warn('[SoleneLife Cart Drawer] Render error:', err);
    }
  }

  // ─── Quantity change ───────────────────────────────────────────────
  async function handleQuantityChange(key, newQuantity) {
    // Optimistic update
    const itemEl = refs.items.querySelector(`[data-key="${CSS.escape(key)}"]`);
    if (itemEl) {
      const countEl = itemEl.querySelector('[data-solene-cd-qty-count]');
      if (countEl) countEl.textContent = newQuantity;
    }

    try {
      let cart = await changeCart(key, newQuantity);

      // If changing a BOGO item, remove orphaned free gift lines when no paid BOGO items remain
      const changedItem = cart.items.find(i => i.key === key);
      if (changedItem && isBogoItem(changedItem)) {
        const hasPaidBogo = cart.items.some(i => isBogoItem(i) && !isFreeBogoItem(i) && i.variant_id === changedItem.variant_id);
        if (!hasPaidBogo) {
          const orphanedFree = cart.items.filter(i => isFreeBogoItem(i) && i.variant_id === changedItem.variant_id);
          for (const freeItem of orphanedFree) {
            cart = await changeCart(freeItem.key, 0);
          }
        }
      }

      renderItems(cart);
      renderFooter(cart);
      renderProgressBar(cart);
      renderUpsell(cart);
      renderState(cart);
    } catch (err) {
      console.warn('[SoleneLife Cart Drawer] Quantity change error:', err);
      // Revert on error
      fullRender(false);
    }
  }

  // ─── Remove with undo ──────────────────────────────────────────────
  async function handleRemove(key) {
    // Find the item to save for undo
    const cart = await fetchCart();
    const item = cart.items.find(i => i.key === key);
    if (!item) return;

    // If removing a BOGO item, also remove associated free gift lines
    // and save total BOGO quantity for correct undo restoration
    let totalBogoQuantity = item.quantity;
    let freeBogoKeys = [];
    if (isBogoItem(item)) {
      const bogoItems = cart.items.filter(i => isBogoItem(i) && i.variant_id === item.variant_id);
      freeBogoKeys = bogoItems.filter(i => isFreeBogoItem(i) && i.key !== key).map(i => i.key);
      totalBogoQuantity = bogoItems.reduce((sum, i) => sum + i.quantity, 0);
    }

    lastRemovedItem = { ...item, quantity: totalBogoQuantity };

    // Optimistic: remove from DOM
    const itemEl = refs.items.querySelector(`[data-key="${CSS.escape(key)}"]`);
    if (itemEl) itemEl.style.display = 'none';
    freeBogoKeys.forEach(freeKey => {
      const freeEl = refs.items.querySelector(`[data-key="${CSS.escape(freeKey)}"]`);
      if (freeEl) freeEl.style.display = 'none';
    });

    // Show undo banner
    showUndoBanner(item.product_title);

    try {
      // Remove the main item
      let updatedCart = await changeCart(key, 0);

      // Also remove any associated free BOGO gift lines.
      // Wrap each in its own try/catch — Shopify may auto-remove the free
      // gift, causing a harmless 400 that must not abort the whole operation
      // or wipe the undo banner via the catch-block's fullRender().
      for (const freeKey of freeBogoKeys) {
        try {
          updatedCart = await changeCart(freeKey, 0);
        } catch (e) { /* free gift may already be removed — ignore */ }
      }

      renderItems(updatedCart);
      renderFooter(updatedCart);
      renderProgressBar(updatedCart);
      renderUpsell(updatedCart);
      renderState(updatedCart);

      // Keep the undo banner visible even when the cart becomes empty
      // (renderState hides body when empty, which would hide the banner)
      if (lastRemovedItem) {
        refs.body.hidden = false;
        refs.empty.hidden = true;
      }
    } catch (err) {
      console.warn('[SoleneLife Cart Drawer] Remove error:', err);
      fullRender(false).then(function () {
        if (lastRemovedItem) {
          showUndoBanner(item.product_title);
          refs.body.hidden = false;
          refs.empty.hidden = true;
        }
      });
    }
  }

  function showUndoBanner(productName) {
    // Remove existing undo banner
    const existing = container.querySelector('.solene-cd-undo');
    if (existing) existing.remove();

    if (undoTimer) clearTimeout(undoTimer);

    const undo = document.createElement('div');
    undo.className = 'solene-cd-undo';
    undo.innerHTML = `<span>Removed. ${esc(productName)}</span><button class="solene-cd-undo-btn" data-solene-cd-undo-btn type="button">Undo</button>`;
    // Insert BEFORE items container so renderItems doesn't wipe it
    refs.body.insertBefore(undo, refs.items);

    undoTimer = setTimeout(() => {
      undo.remove();
      lastRemovedItem = null;
      // Re-evaluate cart state — if cart is empty, show the empty state
      // (handleRemove kept body visible to show the undo banner)
      fetchCart().then(cart => {
        renderState(cart);
      }).catch(() => {});
    }, 5000);
  }

  async function handleUndo() {
    if (!lastRemovedItem) return;
    const item = lastRemovedItem;
    lastRemovedItem = null;
    if (undoTimer) clearTimeout(undoTimer);

    const undo = container.querySelector('.solene-cd-undo');
    if (undo) undo.remove();

    try {
      // Re-add the item with its original properties
      const addBody = { id: item.variant_id, quantity: item.quantity };
      if (item.properties) addBody.properties = item.properties;
      await fetch(cartRoot + 'cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify(addBody),
      });
      fullRender(false);
    } catch (err) {
      console.warn('[SoleneLife Cart Drawer] Undo error:', err);
      fullRender(false);
    }
  }

  // ─── Upsell actions ────────────────────────────────────────────────
  async function handleUpsellAction(action, data) {
    try {
      if (action === 'switch') {
        // Remove current one-time item, add with BOGO offer
        await changeCart(data.key, 0);
        await addToCart(data.variantId, 2, { _fudge: JSON.stringify({ offer: config.bogoOfferToken }) });
        fullRender(false);
      } else if (action === 'bridge') {
        // Add another tube with BOGO offer
        await addToCart(data.variantId, 1, { _fudge: JSON.stringify({ offer: config.bogoOfferToken }) });
        // Show "Added" state
        const btn = refs.upsellCard.querySelector('[data-solene-cd-upsell-action]');
        if (btn) {
          btn.classList.add('solene-cd-upsell-add--added');
          btn.textContent = 'Added';
        }
        setTimeout(() => fullRender(false), 1200);
      }
    } catch (err) {
      console.warn('[SoleneLife Cart Drawer] Upsell error:', err);
      fullRender(false);
    }
  }

  // ─── Close drawer ──────────────────────────────────────────────────
  function closeDrawer() {
    const drawer = document.getElementById('cart-drawer');
    if (drawer && typeof drawer.close === 'function') {
      drawer.close();
    }
  }

  // ─── Auto-open on cart add ─────────────────────────────────────────
  function handleCartLinesUpdate(event) {
    const action = event.action || (event.detail && event.detail.action);
    if (action !== 'add') return;

    // Open the drawer immediately for perceived performance
    // On mobile, skip opening the drawer — the page-level handlers redirect to /cart
    const isMobile = window.matchMedia('(max-width: 749px)').matches;
    if (!isMobile) {
      const drawer = document.getElementById('cart-drawer');
      if (drawer && typeof drawer.open === 'function' && !drawer.hasAttribute('open')) {
        drawer.open();
      }
    }

    // Wait for the promise to resolve, then re-render
    if (event.promise) {
      event.promise
        .then(() => fullRender(false))
        .catch(err => {
          if (err?.name !== 'AbortError') {
            console.warn('[SoleneLife Cart Drawer] Cart update promise rejected:', err);
          }
        });
    } else {
      // No promise, just re-render
      fullRender(false);
    }
  }

  // ─── Mobile drag-to-close ──────────────────────────────────────────
  function setupDragToClose() {
    if (!refs.handle) return;
    const dialog = container.closest('dialog');
    if (!dialog) return;

    let startY = 0;
    let currentY = 0;
    let isDragging = false;

    refs.handle.addEventListener('touchstart', (e) => {
      // Only on mobile
      if (!window.matchMedia('(max-width: 989px)').matches) return;
      startY = e.touches[0].clientY;
      isDragging = true;
      dialog.style.transition = 'none';
    }, { passive: true });

    refs.handle.addEventListener('touchmove', (e) => {
      if (!isDragging) return;
      currentY = e.touches[0].clientY - startY;
      if (currentY > 0) {
        dialog.style.transform = `translateY(${currentY}px)`;
      }
    }, { passive: true });

    refs.handle.addEventListener('touchend', () => {
      if (!isDragging) return;
      isDragging = false;
      dialog.style.transition = '';
      if (currentY > 100) {
        closeDrawer();
        dialog.style.transform = '';
      } else {
        dialog.style.transform = '';
      }
      currentY = 0;
    });

    // Also allow swipe down on the whole header area
    const header = container.querySelector('.solene-cd-header');
    if (header) {
      header.addEventListener('touchstart', (e) => {
        if (!window.matchMedia('(max-width: 989px)').matches) return;
        startY = e.touches[0].clientY;
        isDragging = true;
        dialog.style.transition = 'none';
      }, { passive: true });

      header.addEventListener('touchmove', (e) => {
        if (!isDragging) return;
        currentY = e.touches[0].clientY - startY;
        if (currentY > 0) {
          dialog.style.transform = `translateY(${currentY}px)`;
        }
      }, { passive: true });

      header.addEventListener('touchend', () => {
        if (!isDragging) return;
        isDragging = false;
        dialog.style.transition = '';
        if (currentY > 100) {
          closeDrawer();
          dialog.style.transform = '';
        } else {
          dialog.style.transform = '';
        }
        currentY = 0;
      });
    }
  }

  // ─── Event delegation ──────────────────────────────────────────────
  function setupEventDelegation() {
    // Click events (delegated)
    container.addEventListener('click', (e) => {
      const target = e.target;

      // Close button
      if (target.closest('[data-solene-cd-close]')) {
        e.preventDefault();
        closeDrawer();
        return;
      }

      // Quantity minus
      const minusBtn = target.closest('[data-solene-cd-qty-minus]');
      if (minusBtn) {
        const itemEl = minusBtn.closest('[data-key]');
        if (itemEl) {
          const key = itemEl.dataset.key;
          const qty = parseInt(itemEl.dataset.quantity, 10);
          if (qty > 1) {
            handleQuantityChange(key, qty - 1);
          } else {
            handleRemove(key);
          }
        }
        return;
      }

      // Quantity plus
      const plusBtn = target.closest('[data-solene-cd-qty-plus]');
      if (plusBtn) {
        const itemEl = plusBtn.closest('[data-key]');
        if (itemEl) {
          const key = itemEl.dataset.key;
          const qty = parseInt(itemEl.dataset.quantity, 10);
          handleQuantityChange(key, qty + 1);
        }
        return;
      }

      // Remove
      const removeBtn = target.closest('[data-solene-cd-remove]');
      if (removeBtn) {
        const itemEl = removeBtn.closest('[data-key]');
        if (itemEl) {
          handleRemove(itemEl.dataset.key);
        }
        return;
      }

      // Undo
      if (target.closest('[data-solene-cd-undo-btn]')) {
        handleUndo();
        return;
      }

      // Upsell action
      const upsellBtn = target.closest('[data-solene-cd-upsell-action]');
      if (upsellBtn) {
        const action = upsellBtn.dataset.soleneCdUpsellAction;
        const key = upsellBtn.dataset.key;
        const variantId = upsellBtn.dataset.variantId;
        handleUpsellAction(action, { key, variantId });
        return;
      }

    });

    // ESC key (in case theme-drawer doesn't handle it)
    container.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') {
        closeDrawer();
      }
    });

    // Listen for Shopify cart events
    document.addEventListener('shopify:cart:lines-update', handleCartLinesUpdate);
  }

  // ─── Init ──────────────────────────────────────────────────────────
  function init() {
    setupEventDelegation();
    setupDragToClose();

    // Initial render of dynamic parts (progress bar, shipping, upsell)
    // Don't re-render items on first load (Liquid already rendered them)
    fullRender(true).then(() => {
      isFirstRender = false;
    });
  }

  // Start when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
